import React, { useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { fetchSalesOrderById, fetchOrderDeliveryAddress } from '../../Redux/slice/orderSlice';
import { Modal, Spin, Typography, Row, Col, Button, Divider, Card, Image } from 'antd';
import {
  UserOutlined,
  PhoneOutlined,
  HomeOutlined,
  EditOutlined,
  FileTextOutlined,
  ClockCircleOutlined,
} from '@ant-design/icons';
import PrintableInvoice from './PrintableInvoice';
import { saveAs } from 'file-saver';


import * as XLSX from 'xlsx';

const { Title, Text } = Typography;

const formatPrice = (amount) => {
  return amount.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
};

const OrderDetailsModal = ({ orderId, onClose }) => {
  const dispatch = useDispatch();
  const { salesOrder, loading, error, deliveryAddress } = useSelector((state) => state.orders);

  const printRef = useRef();
  const downloadRef = useRef(null); // New ref for PDF download

  useEffect(() => {
    if (orderId) {
      dispatch(fetchSalesOrderById(orderId));
      dispatch(fetchOrderDeliveryAddress(orderId));
    }
  }, [dispatch, orderId]);

  const handlePrint = () => {
    const printContent = printRef.current;
    const newWindow = window.open('', '_blank');
    newWindow.document.write(printContent.innerHTML);
    newWindow.document.close();
    newWindow.print();
    onClose();
  };

  const exportToExcel = () => {
    if (!salesOrder || salesOrder.length === 0) {
      console.error("No order details available for export.");
      return;
    }
  
    const invoiceData = [
      ["Invoice Details"],
      ["Order No", orderId?.slice(-7) || "N/A"],
      ["Order Date", new Date().toLocaleDateString('en-GB')],
      [],
      ["S.NO", "Product Name", "Quantity", "Unit Price (₵)", "Total Price (₵)"],
      ...salesOrder.map((order, index) => [
        index + 1,
        order.productName,
        order.quantity,
        order.price,
        order.total,
      ]),
    ];
  
    const ws = XLSX.utils.aoa_to_sheet(invoiceData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Invoice");
  
    const excelBuffer = XLSX.write(wb, { bookType: "xlsx", type: "array" });
    const data = new Blob([excelBuffer], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
  
    saveAs(data, `Invoice_${orderId?.slice(-7) || "Unknown"}.xlsx`);
  };
  

  
  if (loading) return <Spin size="large" className='hidden' />;
  if (error) return <div className='hidden'>Error loading order: {error.message || 'An error occurred'}</div>;
  if (!salesOrder || salesOrder.length === 0) return <div>No order details found.</div>;

  const backendBaseURL = 'https://smfteapi.salesmate.app/';
  const totalAmount = salesOrder.reduce((acc, order) => acc + order.total, 0);
  const address = deliveryAddress?.[0] || {};

  return (
    <Modal
    title={
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '5px' }}>
        <div style={{ fontSize: '20px', fontWeight: 'bold', color: '#38A169' }}>
          <FileTextOutlined style={{ marginRight: '10px', color: '#38A169' }} />
          Order Details
        </div>
        <div style={{ fontSize: '16px', display: 'flex', alignItems: 'center', color: '#2D3748' }}>
          <ClockCircleOutlined style={{ marginRight: '5px', color: '#4A5568' }} />
          Date & Time: {new Date(salesOrder[0]?.orderDate).toLocaleString()}
        </div>
      </div>
    }
      visible={true}
      footer={null}
      width={700}
      onCancel={onClose}
      className="rounded-lg shadow-xl"
      style={{
        maxHeight: '85vh',
        overflowY: 'auto',
        borderRadius: '4px',
        scrollbarWidth: 'thin',
  
      }}
      bodyStyle={{
        overflowY: 'auto',
        maxHeight: '70vh',
        padding: '20px',
        scrollbarWidth: 'thin',
      }}
    >
      <style>
        {`
          ::-webkit-scrollbar {
            width: 2px;
          }
          ::-webkit-scrollbar-thumb {
            background-color: #38A169;
            border-radius: 10px;
          }
          ::-webkit-scrollbar-thumb:hover {
            background-color: #2F855A;
          }
          ::-webkit-scrollbar-track {
            background-color: #E53E3E;
          }
        `}
      </style>
      <div ref={downloadRef}>
      {/* Customer Information */}
      <div style={{ background: '#F0FFF4', padding: '15px', borderRadius: '8px', marginBottom: '20px' }}>
        <Title level={5} style={{ color: '#2F855A' }}>
          Customer Information
        </Title>
        <Row gutter={[16, 8]}>
          <Col span={12}>
            <Text strong>
              <UserOutlined /> Name:
            </Text>
            <br />
            <Text>{salesOrder[0]?.fullName}</Text>
          </Col>
          <Col span={12}>
            <Text strong>
              <PhoneOutlined /> Contact:
            </Text>
            <br />
            <Text>{salesOrder[0]?.contactNumber}</Text>
          </Col>
          <Col span={24}>
            <Text strong>
              <HomeOutlined /> Address:
            </Text>
            <br />
            <Text>{salesOrder[0]?.address}</Text>
          </Col>
        </Row>
      </div>

      {/* Delivery Address */}
      <div style={{ background: '#FFF5F5', padding: '15px', borderRadius: '8px', marginBottom: '20px' }}>
        <Title level={5} style={{ color: '#E53E3E' }}>
          Delivery Address
        </Title>
        {address.recipientName ? (
          <Row gutter={[16, 8]}>
            <Col span={12}>
              <Text strong>
                <UserOutlined /> Recipient Name:
              </Text>
              <br />
              <Text>{address.recipientName}</Text>
            </Col>
            <Col span={12}>
              <Text strong>
                <PhoneOutlined /> Contact:
              </Text>
              <br />
              <Text>{address.recipientContactNumber}</Text>
            </Col>
            <Col span={24}>
              <Text strong>
                <HomeOutlined /> Address:
              </Text>
              <br />
              <Text>{address.address}</Text>
            </Col>
            <Col span={24}>
              <Text strong>
                <EditOutlined /> Note:
              </Text>
              <br />
              <Text>{address.orderNote}</Text>
            </Col>
          </Row>
        ) : (
          <Text className="text-gray-500">No delivery address available.</Text>
        )}
      </div>

      {/* Order Details */}
      <div style={{ background: '#F0FFF4', padding: '15px', borderRadius: '8px', marginBottom: '20px' }}>
        <Title level={5} style={{ color: '#2F855A' }}>
          Order Details
        </Title>
        {salesOrder.map((order, index) => {
          const imagePath = order?.imagePath;
          const imageUrl = imagePath
            ? `${backendBaseURL}Media/Products_Images/${imagePath.split('\\').pop()}`
            : null;

          return (
            <Card
              key={index}
              bordered={false}
              className="mb-4 p-4 bg-white shadow-md rounded-lg"
              style={{ marginBottom: '10px' }}
            >
              <Row gutter={16}>
                <Col span={6}>
                  {imageUrl ? (
                    <Image
                      src={imageUrl}
                      alt="Product"
                      style={{ width: '100%', height: 'auto', borderRadius: '6px' }}
                      preview={false}
                    />
                  ) : (
                    <Text className="text-red-600">Image not available.</Text>
                  )}
                </Col>
                <Col span={18}>
                  <Text strong>Product:</Text> {order.productName}
                  <br />
                  <Text strong>Quantity:</Text> {order.quantity}
                  <br />
                  <Text strong>Price:</Text> ₵{formatPrice(order.price)}.00
                  <br />
                  <Text strong>Total:</Text> ₵{formatPrice(order.total)}.00
                  <br />
                  
                </Col>
              </Row>
            </Card>
          );
        })}
      </div>

      {/* Total Amount */}
      <Divider />
      <Row justify="end" style={{ marginBottom: '20px' }}>
        <Text strong style={{ fontSize: '18px', color: '#E53E3E' }}>
          Total Amount: ₵{formatPrice(totalAmount)}.00
        </Text>
      </Row>
      </div>

      {/* Buttons */}
      <Row justify="end" gutter={16}>
  
  <Col>
    <Button
      onClick={handlePrint}
      style={{ background: '#38A169', color: '#FFF', borderColor: '#38A169' }}
    >
      Print Invoice
    </Button>
  </Col>
  <Col>
  <Button
    onClick={exportToExcel}
    style={{ background: '#3182CE', color: '#FFF', borderColor: '#3182CE' }}
  >
    Export to Excel
  </Button>
</Col>
<Col>
    <Button onClick={onClose} style={{ background: '#F7FAFC', color: '#1A202C' }}>
      Close
    </Button>
  </Col>
 
</Row>


      {/* Hidden Printable Invoice */}
      <div ref={printRef} style={{ display: 'none' }}>
        <PrintableInvoice
          orderId={orderId}
          salesOrder={salesOrder}
          deliveryAddress={deliveryAddress}
        />
       
     
      </div>
    </Modal>
  );
};

export default OrderDetailsModal;
